import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BaseBarChart } from "@/components/Charts/BaseBarChart";
import { TargetCostData } from "@/types/target-cost";
import { calculateTargetCostMetrics } from "@/utils/target-cost-calculations";
import { formatCurrency } from "@/utils/formatters";

interface ShareDistributionChartProps {
  data: TargetCostData;
}

export function ShareDistributionChart({ data }: ShareDistributionChartProps) {
  const metrics = calculateTargetCostMetrics(data);
  
  if (!metrics.rangeCalculations.length) return null;

  // Transform range calculations into chart data
  const chartData = metrics.rangeCalculations.map(range => {
    const clientShare = range.amountInRange - range.contractorShare;
    const rangeLabel = range.type === 'less-than' 
      ? `< ${range.endPercentage}%`
      : range.type === 'greater-than'
        ? `> ${range.startPercentage}%`
        : `${range.startPercentage}% - ${range.endPercentage}%`;

    return {
      name: rangeLabel,
      "Contractor's Share": range.contractorShare,
      "Client's Share": clientShare,
      sharePercentage: range.sharePercentage,
      amountInRange: range.amountInRange,
    };
  });
  
  const chartConfig = {
    margin: { top: 20, right: 30, bottom: 60, left: 70 },
    xAxis: {
      angle: -45,
      textAnchor: "end",
      interval: 0,
      fontSize: 11,
      dy: 10,
    },
    yAxis: {
      width: 60,
      tickFormatter: (value: number) => `£${(value / 1000).toFixed(0)}k`,
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pain/Gain Share Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <BaseBarChart
            data={chartData}
            dataKeys={["Contractor's Share", "Client's Share"]}
            colors={[
              'hsl(var(--chart-1))', // Blue for Contractor
              'hsl(var(--muted))' // Gray for Client
            ]}
            margin={chartConfig.margin}
            xAxis={chartConfig.xAxis}
            yAxis={chartConfig.yAxis}
          />
        </div>
      </CardContent>
    </Card>
  );
}